import { OrderLineType } from "../createOrder/createOrderTypes"
import {
    DeliveryOrderLine,
    DynamicConfigKey,
    isLegacyExtraServiceOrderLine,
    isProductWishOrderLine,
} from "../createOrder/components/form/formTypes"

export enum OrderLineGroup {
    PRODUCT = "PRODUCT",
    PRODUCT_WISH = "PRODUCT_WISH",
    EXTRA_SERVICE = "EXTRA_SERVICE",
}

const ORDER_LINE_GROUP_ORDER = [OrderLineGroup.PRODUCT, OrderLineGroup.PRODUCT_WISH, OrderLineGroup.EXTRA_SERVICE]

type OrderLineGroupFlags = Readonly<{ isExtraService: boolean; isProductWish: boolean }>

type TypedOrderLine = Readonly<{ type?: string; productId?: number }>

type DeliveryWithOrderLines<TLine> = Readonly<{ orderLines: ReadonlyArray<TLine> }>

export function getOrderLineGroup({ isExtraService, isProductWish }: OrderLineGroupFlags): OrderLineGroup {
    if (isExtraService) return OrderLineGroup.EXTRA_SERVICE
    if (isProductWish) return OrderLineGroup.PRODUCT_WISH
    return OrderLineGroup.PRODUCT
}

export function sortOrderLinesByGroup<TLine>(
    lines: ReadonlyArray<TLine>,
    getGroup: (line: TLine) => OrderLineGroup,
): Array<TLine> {
    return ORDER_LINE_GROUP_ORDER.flatMap((group) => lines.filter((line) => getGroup(line) === group))
}

export function getDeliveryOrderLineGroup(line: DeliveryOrderLine): OrderLineGroup {
    return getOrderLineGroup({
        isExtraService: Boolean(line.isExtraService) || isLegacyExtraServiceOrderLine(line),
        isProductWish: isProductWishOrderLine(line),
    })
}

export function withOrderLinesGrouped<TLine extends TypedOrderLine, TDelivery extends DeliveryWithOrderLines<TLine>>(
    deliveries: ReadonlyArray<TDelivery>,
) {
    const extraServiceProductIds = new Set(
        deliveries
            .flatMap((delivery) => delivery.orderLines)
            .filter((line) => line.type === OrderLineType.EXTRA_SERVICE)
            .map((line) => line.productId),
    )
    const getGroup = (line: TLine) =>
        getOrderLineGroup({
            isExtraService:
                line.type === OrderLineType.EXTRA_SERVICE ||
                line.type === OrderLineType.LEGACY_EXTRA_SERVICE ||
                (line.productId !== undefined && extraServiceProductIds.has(line.productId)),
            isProductWish: line.type === DynamicConfigKey.PRODUCT_WISH || line.productId === 0,
        })

    return deliveries.map((delivery) => ({
        ...delivery,
        orderLines: sortOrderLinesByGroup(delivery.orderLines, getGroup),
    }))
}
